/**
 * Runtime hostname derivation for the console (ADR 0017).
 *
 * The bundle carries no baked env-specific values. The API base URL
 * and the env label rendered in the header badge are derived from
 * the browser's current `window.location.hostname` at runtime. Same
 * bundle bytes ship to every domain; adding an env is a DNS + CDK
 * change with no rebuild.
 *
 * `CONSOLE_BUILD_ID` stays baked (per-build, not per-env) — computed
 * by the `build` script from `git describe --always --dirty
 * --abbrev=8` + a UTC timestamp so the deployed page can display
 * which commit is live and whether it was built from a dirty tree.
 *
 * The derivation functions are pure and exported for unit tests.
 */

const BASE_DOMAIN = 'witnessengineering.com';
const APP_PREFIX = 'console.';

/**
 * Given a console hostname, returns the matching API base URL, or
 * `null` when the hostname is not one this convention recognizes
 * (localhost, an unrelated domain, a white-label host we haven't
 * added an override for).
 *
 * Rule: strip the leading `console.`; the remainder is
 * `<env-segments>.witnessengineering.com` (or the apex on prod).
 * The API sits at `api.<env-segments>.witnessengineering.com`; when
 * env-segments is empty (prod), the API is `api.witnessengineering.com`.
 */
export function deriveApiBase(hostname: string): string | null {
  if (!hostname.startsWith(APP_PREFIX)) return null;
  const remainder = hostname.slice(APP_PREFIX.length);
  if (remainder === BASE_DOMAIN) return `https://api.${BASE_DOMAIN}`;
  const suffix = `.${BASE_DOMAIN}`;
  if (!remainder.endsWith(suffix)) return null;
  const envSegs = remainder.slice(0, remainder.length - suffix.length);
  if (envSegs.length === 0) return null;
  return `https://api.${envSegs}${suffix}`;
}

/**
 * Env label rendered in the header badge. `'prod'` when the hostname
 * is the recognized prod pattern; the env-segs string (e.g. `'dev'`,
 * `'wonder-hamlet'`) for recognized non-prod; `'unset'` when the
 * hostname does not match the convention (localhost, unrelated
 * domain). Matches the pre-ADR-0017 behavior where a build without
 * env set rendered `unset`.
 */
export function deriveEnvLabel(hostname: string): string {
  if (!hostname.startsWith(APP_PREFIX)) return 'unset';
  const remainder = hostname.slice(APP_PREFIX.length);
  if (remainder === BASE_DOMAIN) return 'prod';
  const suffix = `.${BASE_DOMAIN}`;
  if (!remainder.endsWith(suffix)) return 'unset';
  const envSegs = remainder.slice(0, remainder.length - suffix.length);
  return envSegs.length === 0 ? 'prod' : envSegs;
}

/**
 * Env label for the current browser session. Consumers compare
 * against `'prod'` for styling (prod badge is different from
 * non-prod) and render as display text in the header badge.
 */
export const WITNESS_ENV: string = deriveEnvLabel(
  typeof window !== 'undefined' ? window.location.hostname : '',
);

/**
 * API base URL for the current browser session. `null` when the
 * hostname does not resolve to a known API (localhost, unset). The
 * `witness-api.ts` client throws a clear `ApiNotConfiguredError` at
 * fetch time in that case rather than silently calling nowhere.
 */
export const WITNESS_API_BASE: string | null = deriveApiBase(
  typeof window !== 'undefined' ? window.location.hostname : '',
);

export const CONSOLE_BUILD_ID: string =
  import.meta.env.VITE_CONSOLE_BUILD_ID ?? 'unknown';
